/**
 * ARTIFACT EDIT — style writes, text rewrites and removals replayed against the SOURCE document, never
 * the live one.
 *
 * The inspector renders an artifact in a sandboxed iframe and lets you click text, resize it, and type
 * into it. The obvious implementation, mutate the iframe's DOM and serialize the tree back, is wrong: by
 * the time a document is running it carries the bridge's injected script, whatever inline styles the
 * choreography applied, and the state the animations settled into. Serializing that tree would quietly
 * promote all of it into the stored artifact. ARTIFACTS_PLAN.md §7.1 is blunt about the rule that keeps
 * this honest — `source` is truth, `baked` is cache, and content never lives in two places.
 *
 * So the iframe reports WHERE the user clicked and never WHAT to store. The address is a path of
 * ELEMENT-child indices from `document.body`: text nodes, comments and whitespace are not counted, which
 * is what makes it portable. It is the one coordinate system a running document and a fresh parse of the
 * same source agree on, because the two disagree constantly about text nodes. This module replays that
 * path against a fresh parse of the source string, makes the one edit, and serializes the whole document
 * back. Nothing the author did not write can get in this way.
 *
 * TEXT IS THE ONE THING THE IFRAME IS ALLOWED TO REPORT. New words have no other honest address — they
 * exist nowhere but the running document — so {@link setInnerHtmlAtPath} does take a string from inside
 * the sandbox. That exception is paid for rather than waved through: the string arrives as INNER HTML,
 * because line breaks and highlight spans are content and not decoration, and every byte of it goes
 * through {@link sanitizeArtifactInline} first, which reduces it to a short inline allowlist. What a
 * person typed is what arrives; what survives the allowlist is what gets stored.
 *
 * The price of a positional address is that it is only true of the document it was read from. A style
 * write leaves every path valid; {@link setInnerHtmlAtPath} invalidates the paths INSIDE the element it
 * rewrote; {@link removeAtPath} invalidates a good deal more, and says so at length.
 *
 * Browser-only: DOMParser is the parser and the DOM is the rewriter. Regex never touches the markup —
 * that is the entire point of the module. Never throws: no DOM, an unresolvable path, a broken parse, all
 * of them degrade to null, and null always means "keep the original string".
 */

import DOMPurify from 'dompurify'

/** Whitespace (plus a BOM) the HTML parser skips over before the doctype. */
const LEADING_SPACE = ' \t\n\r\f\uFEFF'

/**
 * The doctype line exactly as the author wrote it, its leading whitespace included, or null when the
 * source has none. Read off the string rather than rebuilt from `doc.doctype`, because the DOM node
 * remembers the name and nothing else — not the casing, and `<!doctype html>` is what we emit. This is
 * string slicing on the PREFIX, not markup rewriting; the document itself is still edited through the DOM.
 */
function leadingDoctype(html: string): string | null {
  let i = 0
  while (i < html.length && LEADING_SPACE.includes(html[i])) i++
  if (html.slice(i, i + 9).toLowerCase() !== '<!doctype') return null
  const end = html.indexOf('>', i)
  return end < 0 ? null : html.slice(0, end + 1)
}

/** A fresh parse of the source. Null without a DOM (SSR, workers, node) and null without a body. */
function parseArtifactDocument(html: string): Document | null {
  if (typeof html !== 'string' || html.trim() === '') return null
  if (typeof DOMParser !== 'function') return null
  const doc = new DOMParser().parseFromString(html, 'text/html')
  if (!doc || !doc.body) return null
  return doc
}

/** The element's inline style declaration, or null for a node that does not carry one. */
export function inlineStyleOf(el: Element): CSSStyleDeclaration | null {
  const style = (el as Partial<ElementCSSInlineStyle>).style
  return style && typeof style.setProperty === 'function' ? style : null
}

/** The tags that ARE the document. No edit here may delete one, whatever path lands on it. */
const STRUCTURAL_TAGS = new Set(['html', 'head', 'body'])

/**
 * The whole edited document as a string: the single serialization path every writer in this module goes
 * through, so a style write and a removal give back the same guarantees. `documentElement.outerHTML` drops
 * the doctype, so the author's doctype line is re-attached from the original source exactly as written.
 * Null when the parse left no root or the serializer gave back nothing — never a partial document.
 */
function serializeArtifactDocument(doc: Document, html: string): string | null {
  const root = doc.documentElement
  if (!root) return null
  const serialized = root.outerHTML
  if (typeof serialized !== 'string' || serialized === '') return null
  const doctype = leadingDoctype(html)
  return doctype === null ? serialized : `${doctype}\n${serialized}`
}

/**
 * Parse once, let `edit` change the document, serialize once: the seam for a write that touches more than
 * one thing on the same parse (a hand edit sets declarations AND its marks). `edit` returns false to
 * refuse, and a refusal, like every other failure here, is null.
 */
export function editArtifactDocument(html: string, edit: (doc: Document) => boolean): string | null {
  try {
    const doc = parseArtifactDocument(html)
    if (!doc || !edit(doc)) return null
    return serializeArtifactDocument(doc, html)
  } catch {
    return null
  }
}

/** CSS property names are ASCII-lowercased unless they are custom properties. Empty names are refused. */
export function normalizeProp(prop: string): string | null {
  if (typeof prop !== 'string') return null
  const name = prop.trim()
  if (name === '') return null
  return name.startsWith('--') ? name : name.toLowerCase()
}

/**
 * Walks an element-child path from `doc.body` and returns the element it addresses.
 *
 * `path` is a chain of ELEMENT-child indices — `[0, 2, 1]` is `body.children[0].children[2].children[1]`.
 * `children` skips text, comments and whitespace, which is exactly why the iframe reports the click this
 * way: the running document and a re-parse of its source count elements identically and text nodes not
 * at all.
 *
 * Null on an empty path, a missing body, a negative or non-integer step, or any step that runs off the
 * end of the children list. Never throws — a caller holding a stale path from a document that has since
 * been re-generated gets null, not an exception.
 */
export function resolveArtifactPath(doc: Document, path: number[]): Element | null {
  try {
    if (!doc || !Array.isArray(path) || path.length === 0) return null
    let current: Element | null = doc.body ?? null
    if (!current) return null
    for (const step of path) {
      if (!Number.isInteger(step) || step < 0) return null
      const next: Element | null = current.children.item(step)
      if (!next) return null
      current = next
    }
    return current
  } catch {
    return null
  }
}

/**
 * The INLINE value of one CSS property on the element at `path`, or null when it is unset, when the path
 * does not resolve, or when there is no DOM to parse with. `prop` is CSS form (`'font-size'`).
 *
 * Inline only, deliberately: the inspector shows what this element itself declares, so a null reads as
 * "inherited from the stylesheet, nothing of its own here" rather than a resolved number the user never
 * wrote. Reading the cascade would need layout, and this runs on the source string, not on a live page.
 */
export function readStyleAtPath(html: string, path: number[], prop: string): string | null {
  try {
    const name = normalizeProp(prop)
    if (name === null) return null
    const doc = parseArtifactDocument(html)
    if (!doc) return null
    const el = resolveArtifactPath(doc, path)
    if (!el) return null
    const style = inlineStyleOf(el)
    if (!style) return null
    const value = style.getPropertyValue(name)
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
  } catch {
    return null
  }
}

/**
 * Sets (or with `value === null` clears) one inline CSS property on the element at `path` and returns the
 * WHOLE document as a new string. The stored source stays authoritative: parse, one DOM write, serialize.
 *
 * Returns null — meaning the caller keeps the string it already has — when the path does not resolve, the
 * property name is empty, the parse produced no body, or there is no DOM. Never a partial document: the
 * only two outcomes are a complete document and null.
 *
 * What survives: everything the parser kept, which is the manifest script and its JSON, comments including
 * the budget line, the stylesheet, every element and its children. `documentElement.outerHTML` drops the
 * doctype, so the original doctype line is re-attached exactly as written. Attribute order survives;
 * attribute QUOTING is the serializer's to normalise (`id=x` comes back as `id="x"`) and so is a decorative
 * self-closing slash on a void tag. Nodes sitting outside `<html>` other than the doctype — a comment
 * before the root element — are not part of the contract and do not survive; nothing we generate has them.
 *
 * An invalid CSS value is dropped by the CSS parser rather than stored, per the DOM spec, so the returned
 * document is well-formed but unchanged. {@link readStyleAtPath} on the result is the caller's check.
 */
/**
 * The tags that are inline BY DEFAULT — the ones whose content is part of the surrounding text rather
 * than a box of its own. Mirrors `INLINE_TAGS` in `docBridge`, deliberately: the bridge makes this same
 * pass over the LIVE document and this one makes it over the source, and the two must agree about what
 * counts as a run or the preview and the stored file would drift.
 *
 * A tag list rather than a computed reading, because there is no layout here — a parsed document has no
 * computed styles. The `display` guard below closes the gap that leaves: an element that has made itself
 * a box says so in its own inline style, and that is visible in the source.
 */
const INLINE_RUN_TAGS = new Set([
  'SPAN', 'A', 'B', 'I', 'EM', 'STRONG', 'U', 'S', 'STRIKE', 'SMALL', 'SUP', 'SUB', 'MARK',
  'CODE', 'ABBR', 'CITE', 'Q', 'VAR', 'KBD', 'SAMP', 'TIME', 'FONT', 'BDI', 'BDO', 'INS', 'DEL',
])

/**
 * STYLING A BLOCK OVERRIDES THE RUNS INSIDE IT, and this is what makes that true of the stored file.
 *
 * A word styled from the text toolbar is a `<span style="…">` inside its block, and a span's inline
 * declaration beats the block's however new the block's is. Without this, restyling a whole headline
 * left that word behind — still red inside a headline the user had just made white — with nowhere to
 * fix it from: an inline run is not separately selectable (see `docBridge`'s `inlineRun`), so the block
 * is the only place its style can be reached. Styling the block has to mean all of it.
 *
 * RUNS ONLY, AND NEVER INTO A BLOCK. A nested block is still selectable in its own right, so its
 * declarations are its own business and its subtree is not walked; an element that has given itself a
 * `display` is such a block whatever its tag says.
 *
 * THE EMPTIED WRAPPER STAYS. Lifting a now-styleless `<span>` out would be tidier markup and is the
 * wrong trade: a path is a chain of ELEMENT-child indices, so removing one element renumbers every
 * sibling after it — and the live document and the source have to agree on those indices or the next
 * edit addresses the wrong node. An inert span is invisible, idempotent under repeated restyles, and
 * costs nothing but bytes. `docBridge` makes the identical pass over the live DOM, and identical is the
 * requirement.
 */
function clearRunProp(el: Element, name: string): void {
  const kids = el.children
  for (let i = 0; i < kids.length; i++) {
    const run = kids[i]
    if (!INLINE_RUN_TAGS.has(run.tagName.toUpperCase())) continue
    const style = inlineStyleOf(run)
    if (style && !style.getPropertyValue('display')) {
      if (style.getPropertyValue(name)) {
        style.removeProperty(name)
        // Same husk rule as the element's own, above: an element reverted to nothing should look like
        // one that was never touched.
        const attr = run.getAttribute('style')
        if (attr !== null && attr.trim() === '') run.removeAttribute('style')
      }
      // A run may wrap another run — a span inside a <b>. Only ever downward through runs.
      clearRunProp(run, name)
    }
  }
}

export function applyStyleAtPath(
  html: string,
  path: number[],
  prop: string,
  value: string | null,
  priority: '' | 'important' = '',
): string | null {
  try {
    const name = normalizeProp(prop)
    if (name === null) return null
    if (value !== null && typeof value !== 'string') return null
    const doc = parseArtifactDocument(html)
    if (!doc) return null
    const el = resolveArtifactPath(doc, path)
    if (!el) return null
    const style = inlineStyleOf(el)
    if (!style) return null

    if (value === null) style.removeProperty(name)
    else style.setProperty(name, value, priority === 'important' ? 'important' : '')

    // Both directions, deliberately. Setting a colour on the block must beat a run's own; CLEARING the
    // block's must not leave a run holding the value the user just reset away from, which would look
    // like the reset had silently skipped one word.
    clearRunProp(el, name)

    // Clearing the last property leaves `style=""` behind in every engine. Drop the husk so an element
    // the user reverted looks exactly like an element they never touched.
    const attr = el.getAttribute('style')
    if (attr !== null && attr.trim() === '') el.removeAttribute('style')

    return serializeArtifactDocument(doc, html)
  } catch {
    return null
  }
}

/**
 * Removes the element at `path`, subtree and all, and returns the WHOLE document as a new string. Same
 * shape as {@link applyStyleAtPath} — parse the source, one DOM write, serialize everything back — and the
 * same guarantees: the doctype comes back exactly as the author wrote it, and every node outside the one
 * that went is untouched.
 *
 * THE HAZARD: REMOVING SHIFTS INDICES, SO EVERY CACHED PATH IS STALE THE MOMENT THIS RETURNS. `path` is a
 * chain of ELEMENT-child indices, and deleting a child closes the gap behind it — each later sibling moves
 * down one, and so does any path that runs through one of them. A path captured before the call that points
 * AT or AFTER the removed node does not fail afterwards; it resolves to the WRONG element, silently, because
 * an index carries no identity. On a non-null return the caller MUST discard what it cached — the current
 * selection, queued or debounced edits, a hover path, anything addressed positionally — and re-derive from
 * the document this returned. Paths addressing an earlier sibling, or a different branch entirely, survive.
 *
 * Returns null, meaning keep the string you already have, when the path does not resolve, the path is empty,
 * the parse produced no body, there is no DOM, or the target is `html`, `head` or `body`. The structural
 * roots are not deletable through this API at any depth: a path landing on one is refused rather than
 * honoured, because a document without a body has stopped being an artifact.
 *
 * Never throws. Null is the only failure.
 */
export function removeAtPath(html: string, path: number[]): string | null {
  try {
    // An empty path addresses `body` itself, which is refused for the same reason the tag check below
    // exists. Named here rather than left to resolveArtifactPath's null, since this call deletes.
    if (!Array.isArray(path) || path.length === 0) return null
    const doc = parseArtifactDocument(html)
    if (!doc) return null
    const el = resolveArtifactPath(doc, path)
    if (!el) return null

    const tag = typeof el.tagName === 'string' ? el.tagName.toLowerCase() : ''
    if (STRUCTURAL_TAGS.has(tag)) return null

    const parent = el.parentNode
    if (!parent || typeof parent.removeChild !== 'function') return null
    parent.removeChild(el)

    return serializeArtifactDocument(doc, html)
  } catch {
    return null
  }
}

/**
 * The tags a text edit may carry back into the source, and nothing else. No anchors, no images, no block
 * elements. `br` and `span` are the two that matter — generated artifacts already use both INSIDE their
 * text elements (birtvyOpenSeed.ts writes `The storytelling workspace<br>for your real work.` into an
 * `<h1>` and `<span class="dot-label">` into a chart label) — and the emphasis tags are there because a
 * contenteditable emits them the moment someone presses the bold shortcut they have pressed all their life.
 */
const INLINE_ALLOWED_TAGS = ['br', 'span', 'em', 'strong', 'b', 'i', 'u', 's', 'small', 'sup', 'sub', 'mark']

/**
 * `class` because the spans that survive a text edit are the CLASSED ones — a highlight span stripped of
 * its class is an artifact silently restyled, which is worse than one visibly broken. `style` because the
 * inspector's own writes land there and a text edit must not undo them. Nothing else: no `id` (the path is
 * the address here, not an id), no `href`, no `src`, and no `on*`, which the allowlist excludes by being
 * an allowlist rather than by naming them.
 */
const INLINE_ALLOWED_ATTR = ['class', 'style']

/** The named entities the text fallback resolves. Anything else is left exactly as written. */
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00a0',
}

/** Character references back to their characters. Out-of-range and lone-surrogate refs are left alone. */
function decodeEntities(text: string): string {
  return text.replace(/&(#[0-9]+|#x[0-9a-f]+|[a-z]+);/gi, (whole: string, ref: string) => {
    if (ref[0] !== '#') return NAMED_ENTITIES[ref.toLowerCase()] ?? whole
    const hex = ref[1] === 'x' || ref[1] === 'X'
    const code = Number.parseInt(hex ? ref.slice(2) : ref.slice(1), hex ? 16 : 10)
    if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return whole
    if (code >= 0xd800 && code <= 0xdfff) return whole
    return String.fromCodePoint(code)
  })
}

/** Text made inert as HTML. `&` first, or the escapes escape each other. */
function escapeText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * The input reduced to inert text, for the one case where DOMPurify cannot run at all — SSR, a worker, a
 * test runner with no DOM. Stripping is the safe direction: prose that lost its line breaks is a smaller
 * loss than markup nobody checked reaching the stored source, and this is the only path that can produce
 * the former.
 *
 * `<script>` and `<style>` go WITH their contents, which is DOMPurify's FORBID_CONTENTS behaviour; every
 * other tag is dropped and its text kept, which is KEEP_CONTENT. Tag matching is deliberately crude — an
 * attribute value carrying a `>` ends the match early — and it is allowed to be, because the residue is
 * escaped before it is returned: a bad cut can only ever leave more TEXT behind, never live markup.
 */
function plainTextOf(html: string): string {
  const stripped = html
    .replace(/<(script|style)\b[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<[^>]*>/g, '')
  return escapeText(decodeEntities(stripped))
}

/**
 * Reduces a string that came out of a contenteditable to the inline subset an artifact is allowed to
 * store: {@link INLINE_ALLOWED_TAGS} and {@link INLINE_ALLOWED_ATTR}, everything else stripped. This is
 * the gate on the one input path that does not originate with the author (see the module header), and the
 * document behind it is both user-facing and persisted, so nothing is trusted on the way through.
 *
 * Disallowed tags are UNWRAPPED, not deleted: DOMPurify's KEEP_CONTENT default keeps their text, so a
 * pasted `<div>hello</div>` stores as `hello` and never as nothing. Someone pasting a paragraph out of a
 * doc should lose the wrapper, not the sentence.
 *
 * TWO GUARDS, BOTH LOAD-BEARING. `window` is what `sanitizeSlideHtml` in utils/sanitizeHtml.ts checks,
 * and it keeps SSR out. `isSupported` is the one that would bite: DOMPurify built without a DOM
 * RETURNS ITS INPUT UNCHANGED (dist/purify.es.mjs, `if (!DOMPurify.isSupported) return dirty`), and on
 * that instance `sanitize` is not even defined, so calling it blind would either throw or write the raw
 * contenteditable string straight into the source. Either way the fallback is {@link plainTextOf}: never
 * throws, and never hands back markup it could not clean.
 */
export function sanitizeArtifactInline(html: string): string {
  try {
    if (typeof html !== 'string' || html === '') return ''
    const usable =
      typeof window !== 'undefined' && DOMPurify.isSupported && typeof DOMPurify.sanitize === 'function'
    if (!usable) return plainTextOf(html)
    const clean = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: INLINE_ALLOWED_TAGS,
      ALLOWED_ATTR: INLINE_ALLOWED_ATTR,
    })
    return typeof clean === 'string' ? clean : plainTextOf(html)
  } catch {
    try {
      return plainTextOf(html)
    } catch {
      return ''
    }
  }
}

/**
 * Replaces the CHILDREN of the element at `path` with `innerHtml`, sanitized, and returns the WHOLE
 * document as a new string. Same shape as the other writers — parse the source, one DOM write, serialize
 * everything back — and the same guarantees: the doctype exactly as the author wrote it, and every node
 * outside the edited element untouched.
 *
 * WHY INNER HTML AND NOT TEXT. Because artifacts put markup inside their text elements, and this repo's
 * own generated content proves it. birtvyOpenSeed.ts writes `The storytelling workspace<br>for your real
 * work.` into the cover `<h1>` and `Pitch 2021, Tome 2023,<br>Gamma 2025, Beautiful.ai 2026.` into a
 * note; it writes `<span class="dot-label">` inside a chart dot and `<b>We are these people.</b>` inside a
 * paragraph. A `<br>` in a headline is not formatting, it is where the line breaks in a layout budgeted to
 * the pixel, and a classed span is what a highlight IS. Reading `textContent` back and storing that would
 * be a one-line change that silently flattened every deliberate line break and dropped every highlight in
 * every artifact anyone had ever edited, and it would look like it worked. Do not "simplify" this to text.
 *
 * The price is that the string is no longer only the author's, so {@link sanitizeArtifactInline} runs on
 * it first and cuts it down to a dozen inline tags and two attributes. A `<script>`, an `onclick`, an
 * `<a href>` and a pasted `<div>` cannot reach the source through here.
 *
 * THE SAME WORDS ARE NOT AN EDIT: when the element's content comes out exactly as it was, the source comes
 * back untouched, the very string that went in. A caret closed without typing still posts its words, and
 * serializing would respell what the author wrote (`crossorigin` as `crossorigin=""`, a raw `&` as `&amp;`, the
 * line breaks between tags), so a close that changed nothing would land a revision all the same (seen
 * 2026-09-27: "edited h1 text", its only difference that spelling). Compared as the parse serializes both
 * sides, so a `<br/>` coming back as `<br>` is the same content too. A caller's own additions on top, like
 * {@link ensureFontLinkInHead}, still land on the untouched string.
 *
 * THE HAZARD, SMALLER THAN {@link removeAtPath}'S BUT REAL: this replaces the element's whole subtree, so
 * any cached path RUNNING THROUGH this element addresses a node that no longer exists. The path to the
 * element itself stays valid, and so does every path to a sibling or another branch — element indices
 * outside it do not move. A caller holding a path to a descendant must re-derive it.
 *
 * Returns null, meaning keep the string you already have, when the path does not resolve, the path is
 * empty, `innerHtml` is not a string, the parse produced no body, there is no DOM, or the target is
 * `html`, `head` or `body` — the same refusal set as {@link removeAtPath}, for the same reason: an empty
 * path addresses `body`, and a document whose body has been replaced by a sentence has stopped being an
 * artifact. The refusal set stops there. A path only ever arrives here for an element the user clicked in
 * the rendered document, so one landing on a raw-text element like `<style>` is out of contract rather
 * than something this guards against.
 *
 * Never throws. Null is the only failure.
 */
export function setInnerHtmlAtPath(html: string, path: number[], innerHtml: string): string | null {
  try {
    if (!Array.isArray(path) || path.length === 0) return null
    if (typeof innerHtml !== 'string') return null
    const doc = parseArtifactDocument(html)
    if (!doc) return null
    const el = resolveArtifactPath(doc, path)
    if (!el) return null

    const tag = typeof el.tagName === 'string' ? el.tagName.toLowerCase() : ''
    if (STRUCTURAL_TAGS.has(tag)) return null

    const before = el.innerHTML
    el.innerHTML = sanitizeArtifactInline(innerHtml)
    if (el.innerHTML === before) return html

    return serializeArtifactDocument(doc, html)
  } catch {
    return null
  }
}

/**
 * One plain phrase for the activity log (ARTIFACTS_PLAN.md §7.2): `'font-size set to 84px'`,
 * `'font-size cleared'`. An empty value reads as cleared because that is what it does — `setProperty`
 * with an empty string removes the property.
 */
/* ── the head, for the one thing that lives there ─────────────────────────────────────────────── */

/**
 * A webfont stylesheet in the artifact's `<head>`, added once.
 *
 * EVERYTHING ELSE IN THIS FILE ADDRESSES THE BODY. `resolveArtifactPath` starts at `doc.body` and
 * refuses `html`, `head` and `body` outright, which is right: an element path is a chain of child
 * indices and the head holds nothing a user can point at. A font is the exception, and it is an
 * exception about LOADING rather than about styling — setting `font-family: 'Clash Display'` on a
 * heading does nothing at all unless the document has a face for it, and the document is a sandboxed
 * iframe on an opaque origin, so nothing the app loads on its own side can reach in.
 *
 * The contract already sanctions the shape: "A Google Fonts <link> in the head is allowed and loads
 * fine; name only the weights you actually use" (`artifactContract.ts`). `validateArtifact` has no
 * rule that fires on one, and the bake carries link hrefs forward as `data-bds-fonts`, so a font added
 * this way survives serialization the same way an authored one does.
 *
 * BEFORE `</head>`, NEVER AFTER `<head>`, and `guaranteePackageTags` is the precedent this is modelled
 * on line for line. The contract requires the manifest to be the first thing inside the head, so a tag
 * prepended there would displace it.
 *
 * A STREAMING PARTIAL whose head is not closed yet comes back UNTOUCHED, for that function's reason:
 * there is nowhere safe to put a tag in half a document.
 *
 * Returns the source UNCHANGED when the href is already there, so picking the same font twice writes
 * nothing and the funnel records no revision for it.
 */
export function ensureFontLinkInHead(html: string, href: string): string {
  if (!html || !href) return html

  // Escaped rather than trusted. The href is built from a catalogue entry today, and a value that closed
  // its own attribute would write arbitrary markup into a document this app then stores and re-renders.
  const safe = href.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')

  /*
   * ALREADY THERE? CHECK BOTH SPELLINGS, and the escaped one first — because it is the one THIS
   * function writes. Comparing only the raw href appended a duplicate link on every re-pick: a css2 URL
   * carries an `&` before `display=swap`, so what went into the document never matched what came back
   * in. Caught by the idempotence test, which is the only reason it is not still doing that.
   *
   * The raw form is checked too, since a model-authored head writes its own links unescaped.
   *
   * Compared on the whole href rather than on the family: two requests for one family at different
   * weights are two different stylesheets, and a document may legitimately want both.
   */
  if (html.includes(safe) || html.includes(href)) return html

  const headClose = html.search(/<\/head\s*>/i)
  if (headClose < 0) return html

  return `${html.slice(0, headClose)}<link rel="stylesheet" href="${safe}">\n${html.slice(headClose)}`
}

export function describeStyleEdit(prop: string, value: string | null): string {
  const name = typeof prop === 'string' && prop.trim() !== '' ? prop.trim() : 'style'
  const shown = typeof value === 'string' ? value.trim() : ''
  return shown === '' ? `${name} cleared` : `${name} set to ${shown}`
}

/** How much of an element's text a log line quotes, removals and text edits alike. Past this it stops
 *  being a glance. */
const LOG_TEXT_MAX = 40

/** Element text as a log line quotes it: whitespace collapsed, cut at {@link LOG_TEXT_MAX}, no dangling
 *  space before the ellipsis. Empty in, empty out — the callers decide what an empty excerpt reads as. */
function clipLogText(text: unknown): string {
  const shown = typeof text === 'string' ? text.trim().replace(/\s+/g, ' ') : ''
  if (shown === '') return ''
  return shown.length > LOG_TEXT_MAX ? `${shown.slice(0, LOG_TEXT_MAX).trimEnd()}…` : shown
}

/**
 * One plain phrase for the activity log (ARTIFACTS_PLAN.md §7.2): `'removed h1 "95% of elite roles"'`, or
 * `'removed div'` for an element that carried no text of its own. The text is what makes the line useful —
 * a log of six `removed div` rows tells the user nothing about which div went.
 *
 * `tag` is lowercased, because a live DOM hands back `el.tagName` shouting (`'H1'`) and the log is prose.
 * The excerpt is whitespace-collapsed, since element text arrives carrying the source's newlines and
 * indentation, and cut at {@link LOG_TEXT_MAX} characters: the line is a reminder of what went, not a
 * copy of it. Quotes appear only when there is something inside them.
 */
export function describeRemoveEdit(tag: string, text?: string): string {
  const name = typeof tag === 'string' && tag.trim() !== '' ? tag.trim().toLowerCase() : 'element'
  const clipped = clipLogText(text)
  return clipped === '' ? `removed ${name}` : `removed ${name} "${clipped}"`
}

/**
 * One plain phrase for the activity log (ARTIFACTS_PLAN.md §7.2): `'edited h1 text to "95% of elite
 * roles"'`, or `'cleared h1 text'` when the user emptied it — which is what happened, and reads better in
 * a log than `edited h1 text` with nothing after it.
 *
 * `text` is the element's TEXT, not the inner HTML that was stored: the log is prose, and a line reading
 * `edited h1 text to "95% of<br>elite roles"` would be a worse reminder than a line without the tag in it.
 * Formatting matches {@link describeRemoveEdit} exactly — tag lowercased, whitespace collapsed, cut at
 * {@link LOG_TEXT_MAX} characters — because the two lines sit next to each other in the same list.
 */
export function describeTextEdit(tag: string, text: string): string {
  const name = typeof tag === 'string' && tag.trim() !== '' ? tag.trim().toLowerCase() : 'element'
  const clipped = clipLogText(text)
  return clipped === '' ? `cleared ${name} text` : `edited ${name} text to "${clipped}"`
}
